import { useLayerFromDualInstance } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import { useEffect, useRef, useState } from 'react';
import {
  isNewRegisterSessionRandomizable,
  NEW_REGISTER_DESIGN_LAYER_VARIANTS,
} from '@shared/utils/newRegisterDesign';
import {
  ONBOARDING_BROWSER_FLOWS_LAYER,
  ONBOARDING_BROWSER_FLOWS_VARIANT_PARAM,
} from './onboardingBrowserFlowsLayer';

export const NEW_REGISTER_DESIGN_MAX_WAIT_MS = 1500;
export type NewRegisterDesignStatus = 'pending' | 'control' | 'treatment';
const NEW_REGISTER_DESIGN_SESSION_KEY =
  'descope-universal-login:new-register-design';
const NEW_REGISTER_DESIGN_VARIANT_SESSION_KEY =
  'descope-universal-login:new-register-design-variant';

const readStoredDesignDecision = (): boolean | undefined => {
  try {
    const storedDecision = sessionStorage.getItem(
      NEW_REGISTER_DESIGN_SESSION_KEY
    );
    return storedDecision === null ? undefined : storedDecision === 'true';
  } catch {
    return undefined;
  }
};

const writeStoredDesignDecision = (decision: boolean): void => {
  try {
    sessionStorage.setItem(NEW_REGISTER_DESIGN_SESSION_KEY, String(decision));
  } catch {
    // Blocked storage degrades to the legacy screen consistently.
  }
};

const readStoredDesignVariant = (): string | null => {
  try {
    return sessionStorage.getItem(NEW_REGISTER_DESIGN_VARIANT_SESSION_KEY);
  } catch {
    return null;
  }
};

const writeStoredDesignVariant = (variant: string | null): void => {
  try {
    if (variant === null) {
      sessionStorage.removeItem(NEW_REGISTER_DESIGN_VARIANT_SESSION_KEY);
    } else {
      sessionStorage.setItem(NEW_REGISTER_DESIGN_VARIANT_SESSION_KEY, variant);
    }
  } catch {
    // Blocked storage degrades to the legacy screen consistently.
  }
};

export { NEW_REGISTER_DESIGN_LAYER_VARIANTS };

export { isNewRegisterSessionRandomizable };

export const useNewRegisterDesignLayer = (
  screenClassification: {
    isRegisterScreen: boolean;
    isSignupContinuationScreen: boolean;
  },
  isRandomizableSession: boolean,
  onboardingVariant: string | null = null,
  onResolvedVariant?: (variant: string | null) => void
): NewRegisterDesignStatus => {
  const { getLayerParameterFromWarehouseNative, initialised } =
    useLayerFromDualInstance(ONBOARDING_BROWSER_FLOWS_LAYER);
  // Non-randomizable sessions are excluded because reading the shared layer
  // would pollute experiment exposures for an explicitly selected arm.
  const { isRegisterScreen, isSignupContinuationScreen } = screenClassification;
  const isEligibleScreen =
    isRegisterScreen && isRandomizableSession && onboardingVariant === null;
  const hasReadLayerRef = useRef(false);
  const showNewRegisterDesignRef = useRef(false);
  const resolvedVariantRef = useRef<string | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!isEligibleScreen || initialised || hasTimedOut) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => setHasTimedOut(true),
      NEW_REGISTER_DESIGN_MAX_WAIT_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [hasTimedOut, initialised, isEligibleScreen]);

  if (
    isEligibleScreen &&
    initialised &&
    !hasTimedOut &&
    !hasReadLayerRef.current
  ) {
    hasReadLayerRef.current = true;
    const resolvedVariant = String(
      getLayerParameterFromWarehouseNative(
        ONBOARDING_BROWSER_FLOWS_VARIANT_PARAM,
        ''
      )
    );
    resolvedVariantRef.current = resolvedVariant || null;
    showNewRegisterDesignRef.current =
      NEW_REGISTER_DESIGN_LAYER_VARIANTS.has(resolvedVariant);
  } else if (
    isRegisterScreen &&
    !hasReadLayerRef.current &&
    !isRandomizableSession
  ) {
    hasReadLayerRef.current = true;
    showNewRegisterDesignRef.current = false;
  } else if (isSignupContinuationScreen && !hasReadLayerRef.current) {
    const storedDecision = readStoredDesignDecision();
    if (storedDecision !== undefined) {
      hasReadLayerRef.current = true;
      showNewRegisterDesignRef.current = storedDecision;
      resolvedVariantRef.current = readStoredDesignVariant();
    }
  }

  const isLayerTreatment =
    hasReadLayerRef.current && showNewRegisterDesignRef.current;
  const shouldPersistDecision =
    (isRegisterScreen &&
      (!isRandomizableSession || initialised || hasTimedOut)) ||
    (isSignupContinuationScreen && onboardingVariant !== null);

  useEffect(() => {
    if (shouldPersistDecision) {
      writeStoredDesignDecision(
        onboardingVariant !== null
          ? NEW_REGISTER_DESIGN_LAYER_VARIANTS.has(onboardingVariant)
          : isLayerTreatment
      );
      writeStoredDesignVariant(onboardingVariant ?? resolvedVariantRef.current);
    }
  }, [
    hasTimedOut,
    initialised,
    isLayerTreatment,
    onboardingVariant,
    shouldPersistDecision,
  ]);

  useEffect(() => {
    if (
      onResolvedVariant &&
      (onboardingVariant !== null || hasReadLayerRef.current)
    ) {
      onResolvedVariant(onboardingVariant ?? resolvedVariantRef.current);
    }
  }, [onboardingVariant, onResolvedVariant, isLayerTreatment]);

  if (!isRegisterScreen && !isSignupContinuationScreen) {
    return 'control';
  }

  if (!isRandomizableSession) {
    return 'control';
  }

  if (onboardingVariant !== null) {
    return NEW_REGISTER_DESIGN_LAYER_VARIANTS.has(onboardingVariant)
      ? 'treatment'
      : 'control';
  }

  if (isSignupContinuationScreen) {
    return hasReadLayerRef.current && showNewRegisterDesignRef.current
      ? 'treatment'
      : 'control';
  }

  if (!isEligibleScreen) {
    return 'control';
  }

  if (!initialised && !hasTimedOut) {
    return 'pending';
  }

  return isLayerTreatment ? 'treatment' : 'control';
};
